import { Dialog, DialogActions, Button, Box, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import { getColDefaultValue } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { arrType, inputTypes, selectOptionsKeyName } from "data/globalData";
import { trimsMasterColumnsObj, trimsSampleColumnsObj, trimDesignerDropdowns } from "../../data";

const SampleRowModal = ({ open, setOpen, mode = "create", selectedRow, onSave = () => {}, selectedTrim = {} }) => {
	const { snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const defPageData = Object.values(trimsSampleColumnsObj)
		.map((col) => ({ [col.key]: getColDefaultValue(col) }))
		.reduce((acc, row) => ({ ...acc, ...row }), {});

	const [pageData, setPageData] = useState(defPageData);
	const [errorObj, setErrorObj] = useState({});

	useEffect(() => {
		if (mode === "update" && selectedRow) setPageData(selectedRow);
		if (mode === "create") setPageData(defPageData);
	}, [selectedRow, mode, open]);

	const handleClose = () => {
		setOpen(false);
		setPageData(defPageData);
		setErrorObj({});
	};

	const localApprovedByColumn = useMemo(() => {
		if (!selectedTrim?.[trimsMasterColumnsObj.brand.key]) return trimsSampleColumnsObj.approved_by;

		return {
			...trimsSampleColumnsObj.approved_by,
			[selectOptionsKeyName]: {
				type: arrType,
				list: trimDesignerDropdowns.filter(
					(item) => item[trimsMasterColumnsObj.brand.key] === selectedTrim[trimsMasterColumnsObj.brand.key],
				),
			},
		};
	}, [selectedTrim?.[trimsMasterColumnsObj.brand.key]]);

	const handleSave = () => {
		let errObj = {};
		Object.values(trimsSampleColumnsObj).forEach((col) => {
			if (col.input_type === inputTypes.AUTO_GENERATE.key) return;
			if (col.constraints?.NOTNULL && !pageData[col.key]) {
				errObj = { ...errObj, [col.key]: `${col.name} is required` };
			}
		});

		const approvedByFilled = !!pageData?.[trimsSampleColumnsObj.approved_by.key];
		const approvalDateFilled = !!pageData?.[trimsSampleColumnsObj.date_of_approval.key];
		if (approvedByFilled !== approvalDateFilled) {
			errObj = {
				...errObj,
				[trimsSampleColumnsObj.approved_by.key]: "Approved By and Approval Date must be filled together",
				[trimsSampleColumnsObj.date_of_approval.key]: "Approved By and Approval Date must be filled together",
			};
		}

		if (Object.keys(errObj).length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "warning",
				message: "Please fill the required fields",
			});
			setErrorObj(errObj);
			return;
		}

		onSave({ ...pageData });
		handleClose();
	};

	return (
		<Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose} scroll="body">
			<Stack spacing={2} p={2}>
				<Typography variant="h6">{mode === "create" ? "Add" : "Update"} Sample</Typography>
				<Box display="flex" flexWrap="wrap" gap="3%">
					<RenderInputField
						column={trimsSampleColumnsObj.sample_etd_date}
						data={pageData}
						setData={setPageData}
						width="45%"
						error={errorObj[trimsSampleColumnsObj.sample_etd_date.key]}
					/>
					<RenderInputField
						column={trimsSampleColumnsObj.sample_delivery_status}
						data={pageData}
						setData={setPageData}
						width="45%"
						selectOptObjName={selectOptionsKeyName}
						error={errorObj[trimsSampleColumnsObj.sample_delivery_status.key]}
					/>
					<RenderInputField
						column={trimsSampleColumnsObj.sample_delivery_date}
						data={pageData}
						setData={setPageData}
						width="45%"
						error={errorObj[trimsSampleColumnsObj.sample_delivery_date.key]}
					/>
					<RenderInputField
						column={trimsSampleColumnsObj.approval}
						data={pageData}
						setData={setPageData}
						width="45%"
						selectOptObjName={selectOptionsKeyName}
						error={errorObj[trimsSampleColumnsObj.approval.key]}
					/>
					<RenderInputField
						column={localApprovedByColumn}
						data={pageData}
						setData={setPageData}
						width="45%"
						selectOptObjName={selectOptionsKeyName}
						error={errorObj[localApprovedByColumn.key]}
					/>
					<RenderInputField
						column={trimsSampleColumnsObj.date_of_approval}
						data={pageData}
						setData={setPageData}
						width="45%"
						error={errorObj[trimsSampleColumnsObj.date_of_approval.key]}
					/>
					<RenderInputField
						column={trimsSampleColumnsObj.remarks}
						data={pageData}
						setData={setPageData}
						width="100%"
						error={errorObj[trimsSampleColumnsObj.remarks.key]}
					/>
				</Box>
			</Stack>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleSave}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SampleRowModal;
